<template>
  <section class="about-section">
    <div class="container">

      <!-- Stats bar -->
      <div class="stats-row" ref="statsRef">
        <div class="stat-item" v-for="(stat, i) in stats" :key="i" v-reveal="i * 100">
          <span class="stat-num">{{ stat.display }}</span>
          <span class="stat-label">{{ $t(stat.labelKey) }}</span>
        </div>
      </div>

      <div class="row justify-content-center mt-5">
        <div class="col-lg-9 text-center about-col">
          <span class="section-label" v-reveal>{{ $t('about.label') }}</span>
          <h2 class="section-title" v-reveal="80">{{ $t('about.title') }}</h2>
          <p class="about-bio" v-reveal="140">{{ $t('about.bio1') }}</p>
          <p class="about-bio" v-reveal="200">{{ $t('about.bio2') }}</p>

          <div class="about-highlights" v-reveal="260">
            <div class="highlight-item">
              <i class="fas fa-check-circle"></i>
              <span>{{ $t('about.highlight1') }}</span>
            </div>
            <div class="highlight-item">
              <i class="fas fa-check-circle"></i>
              <span>{{ $t('about.highlight2') }}</span>
            </div>
            <div class="highlight-item">
              <i class="fas fa-check-circle"></i>
              <span>{{ $t('about.highlight3') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const statsRef = ref(null)

const stats = [
  { display: '20+', labelKey: 'about.stat1_label' },
  { display: '5+',  labelKey: 'about.stat2_label' },
  { display: '1',   labelKey: 'about.stat3_label' },
]

// Animated counters
onMounted(() => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        animateCounters()
        observer.disconnect()
      }
    },
    { threshold: 0.4 }
  )
  if (statsRef.value) observer.observe(statsRef.value)
})

function animateCounters() {
  const targets = [20, 5, 1]
  const suffixes = ['+', '+', '']
  document.querySelectorAll('.stat-num').forEach((el, i) => {
    const end = targets[i]
    const suffix = suffixes[i]
    const duration = 1200
    const step = Math.ceil(end / (duration / 16))
    let current = 0
    const timer = setInterval(() => {
      current = Math.min(current + step, end)
      el.textContent = current + suffix
      if (current >= end) clearInterval(timer)
    }, 16)
  })
}
</script>

<style scoped>
/* Warm feathered veil: subtle amber-tinted panel for colour rhythm,
   edges fade out so the page keeps flowing. */
.about-section {
  padding: 7rem 0 6rem;
  background: linear-gradient(180deg,
    rgba(38, 26, 10, 0) 0%,
    rgba(38, 26, 10, 0.55) 16%,
    rgba(38, 26, 10, 0.55) 84%,
    rgba(38, 26, 10, 0) 100%);
}

/* ─── Stats ─────────────────────────────────── */
.stats-row {
  display: flex;
  justify-content: center;
  gap: 0;
  background: linear-gradient(180deg, #151515, #0e0e0e);
  border: 1px solid var(--border-strong);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 0;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.stat-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1.5rem;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  text-align: center;
  transition: background 0.2s;
}

.stat-item:last-child { border-right: none; }

.stat-item:hover {
  background: var(--accent-tint);
}

.stat-num {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: 700;
  color: #fff;
  line-height: 1;
  margin-bottom: 0.4rem;
  background: linear-gradient(135deg, var(--accent-soft), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

/* ─── Labels ─────────────────────────────────── */
.section-label {
  display: inline-block;
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 0.8rem;
  color: var(--accent);
  letter-spacing: 1px;
  margin-bottom: 0.875rem;
}

.section-title {
  font-family: 'Jersey 15', sans-serif;
  font-size: 2.5rem;
  color: #fff;
  font-weight: 400;
  margin-bottom: 1.5rem;
  letter-spacing: 0;
}

.about-bio {
  color: var(--text-muted);
  font-size: 1.05rem;
  line-height: 1.85;
  margin-bottom: 1.25rem;
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;
}

.about-highlights {
  margin-top: 2.25rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem 1rem;
}

.highlight-item {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  color: var(--text-soft);
  font-size: 0.9rem;
  background: #111111;
  border: 1px solid var(--border-strong);
  border-radius: 100px;
  padding: 0.5rem 1.1rem;
  transition: border-color 0.2s, transform 0.15s;
}

.highlight-item:hover { border-color: var(--accent-line); transform: translateY(-2px); }

.highlight-item i { color: var(--accent); font-size: 1rem; flex-shrink: 0; }

/* ─── Stack card ─────────────────────────────── */
.stack-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  padding: 2rem 2.25rem;
}

.stack-card-title {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1.75rem;
}

.stack-group { margin-bottom: 1.5rem; }
.stack-group:last-child { margin-bottom: 0; }

.stack-group-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.6rem;
}

.stack-badges { display: flex; flex-wrap: wrap; gap: 0.5rem; }

.badge-tech {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.35rem 0.8rem;
  border-radius: 6px;
  border: 1px solid transparent;
  transition: transform 0.15s, box-shadow 0.15s;
  cursor: default;
}

/* Unified neutral badges — amber lights up on hover */
.badge-tech {
  background: var(--surface-3);
  border-color: var(--border);
  color: var(--text-soft);
}

.badge-tech:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
  border-color: var(--accent-line);
  color: var(--accent-soft);
}

.badge-tech i { color: var(--accent); transition: color 0.15s; }

/* legacy colour classes now fold into the unified look */
.badge-blue, .badge-green, .badge-red, .badge-orange,
.badge-green-dark, .badge-blue-dark, .badge-purple, .badge-orange-ai {
  background: var(--surface-3);
  border-color: var(--border);
  color: var(--text-soft);
}

@media (max-width: 991px) { .about-section { padding: 5rem 0 4rem; } }
@media (max-width: 767px) {
  .about-section { padding: 4rem 0 3rem; }
  .section-title { font-size: 1.875rem; }
  .about-bio { font-size: 0.95rem; }
  .stack-card { padding: 1.5rem; }
  .stack-card-title { margin-bottom: 1.25rem; }
  .stack-group { margin-bottom: 1.25rem; }
  .badge-tech { font-size: 0.78rem; }
  .stats-row { flex-direction: column; }
  .stat-item { border-right: none; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
  .stat-item:last-child { border-bottom: none; }
}
</style>
